// Type-only import: `HttpErrorResponse` is never used as a value here, only as an annotation, so it
// must not become a runtime import — pulling in `@angular/common/http` breaks this lib's plain
// (non-Angular) Jest transform, which has no ESM/.mjs handling for it.
import type { HttpErrorResponse } from '@angular/common/http';
import { ApiError } from '@shared/definitions';

/**
 * Decodes the error body of a failed `responseType: 'blob'` request back into an `ApiError`, so it
 * can be passed straight to `alertService.handleApiError(...)`.
 *
 * With `responseType: 'blob'` a 400 body arrives as a `Blob` rather than the parsed JSON object
 * HttpClient gives every other request, so the usual `err?.error?.error` idiom resolves to
 * `undefined` and the alert bar shows nothing. Never throws — a malformed, empty, or non-JSON blob
 * resolves to `null` so the caller can fall back to a generic message.
 *
 * Accepts either the caught `HttpErrorResponse` itself or its already-unwrapped `error` value, so
 * it also works as a drop-in replacement for `err?.error?.error` on requests that were never blobs.
 */
export const readBlobApiError = async (source: HttpErrorResponse | unknown): Promise<ApiError | null> => {
  // An HttpErrorResponse always carries `status`; a Blob or an already-parsed `{ error }` body
  // never does — that tells them apart without an `instanceof` check on the Angular class itself.
  const isHttpErrorResponse = !!source && typeof source === 'object' && 'status' in source && 'error' in source;
  const body = isHttpErrorResponse ? (source as HttpErrorResponse).error : source;

  if (!(body instanceof Blob)) {
    return (body as { error?: ApiError })?.error ?? null;
  }

  try {
    // `FileReader` over `Blob.text()`/`.arrayBuffer()`: both are unimplemented on the `Blob` the
    // Angular/jsdom test environment provides, while `FileReader` works there and in every browser.
    const text = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(body);
    });
    const parsed = JSON.parse(text);
    return parsed?.error ?? null;
  } catch {
    return null;
  }
};
